import { Spacing } from '@persona-id/design-tokens';
import { ReactNode, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';

import ImageFlag from 'images/inquiry/id-class/flag.svg';
import ImageGlobe from 'images/inquiry/id-class/globe.svg';
import ImageHome from 'images/inquiry/id-class/home.svg';
import ImageIdHorizontal from 'images/inquiry/id-class/id-horizontal.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import VerticalOption from 'inquiry/components/VerticalOption/VerticalOption';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { textForGovernmentId } from 'inquiry/lib/inquiry';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { colorCss } from 'lib/inquiryStyles';
import {
  SerializedStepGovernmentIdStyles,
  StepGovernmentIdConfig,
} from 'lib/interfaces/StepsConfig';
import { IconStyle } from 'lib/interfaces/ThemeSerialized';
import {
  GovernmentIdEnabledIdClassByCountryEntry,
  GovIdIcon,
} from 'lib/interfaces/verificationConfig';

const ThemedIcon = styled('span')`
  rect[fill]:not([fill='none']) {
    fill: ${({ theme }) => theme.colorPrimary};
  }

  path[fill]:not([fill='none']) {
    stroke: ${({ theme }) => theme.iconColorBackground};
  }

  height: 32px;
`;

const SSTThemedIcon = styled('span')<{
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  styles: SerializedStepGovernmentIdStyles['selectOption']['icon'];
}>`
  rect[fill]:not([fill='none']) {
    fill: ${({ styles }) => colorCss(styles.fill.color)};
  }

  path[stroke]:not([stroke='none']) {
    stroke: ${({ styles }) => colorCss(styles.stroke.color)};
  }

  height: 32px;
`;

const IconWrapper = ({
  styles,
  children,
  'data-test': dataTest,
}: {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  styles: SerializedStepGovernmentIdStyles['selectOption']['icon'];
  children: ReactNode;
  'data-test'?: string;
}) => {
  return (
    <>
      {styles != null ? (
        <SSTThemedIcon data-test={dataTest} styles={styles} aria-hidden>
          {children}
        </SSTThemedIcon>
      ) : (
        <ThemedIcon data-test={dataTest} aria-hidden>
          {children}
        </ThemedIcon>
      )}
    </>
  );
};

const OptionVerticalButton = styled(Button)`
  width: 100%;
  & + & {
    margin-top: ${Spacing[5]};
  }
`;

const IconContainer = styled('span')`
  line-height: 0;
  margin-right: 10px;
`;

interface OptionVerticalProps {
  assets: StepGovernmentIdConfig['assets']['select-page'];
  buttonType: 'outline' | 'outline_light' | 'button';
  countryCode?: CountryCode;
  handleSelectType: (type: string) => void;
  governmentIdClass: GovernmentIdClass;
  icon: GovernmentIdEnabledIdClassByCountryEntry['icon'];
  localizations: StepGovernmentIdConfig['localizations']['select-page'];
  styles?: SerializedStepGovernmentIdStyles['selectOption'];
}

export default function OptionVertical({
  assets,
  buttonType,
  countryCode,
  handleSelectType,
  governmentIdClass,
  icon,
  localizations,
  styles,
}: OptionVerticalProps) {
  const { iconStyle } = useContext(ThemeContext);
  const { buttons: buttonStyles } = useStepGovernmentIdStyleContext();

  const childProps = {
    id: `select__option--${governmentIdClass}`,
    'data-test': 'option-block',
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onClick: () => handleSelectType(governmentIdClass),
  };

  if (buttonType === 'button') {
    return (
      <OptionVerticalButton
        {...childProps}
        styles={buttonStyles?.[ButtonType.Primary]}
        type='button'
      >
        {textForGovernmentId(localizations, countryCode, governmentIdClass)}
      </OptionVerticalButton>
    );
  }

  return (
    <VerticalOption
      {...childProps}
      buttonType={buttonType}
      styles={styles}
      icon={
        iconStyle !== IconStyle.None && (
          <IconContainer data-test='option-vertical__pictograph'>
            <OptionIcon
              governmentIdClass={governmentIdClass}
              assets={assets}
              icon={icon}
              styles={styles?.icon}
            />
          </IconContainer>
        )
      }
    >
      {textForGovernmentId(localizations, countryCode, governmentIdClass)}
    </VerticalOption>
  );
}

const OptionIcon = ({
  assets,
  governmentIdClass,
  icon,
  styles,
}: Pick<OptionVerticalProps, 'assets' | 'governmentIdClass' | 'icon'> & {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  styles?: SerializedStepGovernmentIdStyles['selectOption']['icon'];
}) => {
  const primaryAssetByConfig = icon?.['custom-icon'];
  const primaryAssetByIdClass = customAssetByIdClass(assets, governmentIdClass);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const fallbackAssetByConfig = imageForGovIdIcon(icon?.['icon-fallback']);
  const fallbackAssetByIdClass = fallbackIconByIdClass(governmentIdClass);
  const primaryAsset = primaryAssetByConfig ?? primaryAssetByIdClass;
  const fallbackAsset = fallbackAssetByConfig ?? fallbackAssetByIdClass;

  return (
    <CustomAssetWithFallback customImage={primaryAsset} alt='government id icon'>
      <IconWrapper data-test='option-vertical-icon__fallback' styles={styles}>
        {fallbackAsset}
      </IconWrapper>
    </CustomAssetWithFallback>
  );
};

function customAssetByIdClass(
  assets: OptionVerticalProps['assets'],
  governmentIdClass: OptionVerticalProps['governmentIdClass'],
) {
  switch (governmentIdClass) {
    case 'dl':
    case 'id':
      return assets['icon-drivers-license'];
    case 'ofw':
    case 'pp':
    case 'ppc':
    case 'visa':
      return assets['icon-passport'];

    case 'rp':
    case 'pr':
      return assets['icon-permanent-residence-id'] ?? assets['icon-government-id'];
    default:
      return assets['icon-government-id'];
  }
}

function fallbackIconByIdClass(governmentIdClass: OptionVerticalProps['governmentIdClass']) {
  switch (governmentIdClass) {
    case 'dl':
    case 'id':
      return <ImageIdHorizontal />;
    case 'ofw':
    case 'pp':
    case 'ppc':
    case 'visa':
      return <ImageGlobe />;

    case 'rp':
    case 'pr':
    default:
      return <ImageHome />;
  }
}

function imageForGovIdIcon(icon: GovIdIcon) {
  switch (icon) {
    case GovIdIcon.Flag:
      return <ImageFlag />;
    case GovIdIcon.World:
      return <ImageGlobe />;
    case GovIdIcon.House:
      return <ImageHome />;
    case GovIdIcon.Card:
      return <ImageIdHorizontal />;
    default:
      return null;
  }
}
